// packages/web/src/hooks/useUser.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService} from '@/services/user.service';
import { User as UserProfile } from '@bookstore/shared';
import { toast } from 'sonner';
import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Hook: Get user profile by ID
 */
export const useUserProfile = (userId: string | undefined) => {
  return useQuery<UserProfile | null>({
    queryKey: ['user-profile', userId],
    queryFn: () => userService.getUserProfile(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook: Get current logged-in user's profile
 */
export const useCurrentUser = () => {
  const { firebaseUser, loading } = useAuth();
  const currentUserId = firebaseUser?.uid;

  return useQuery<UserProfile | null>({
    queryKey: ['current-user', currentUserId],
    queryFn: () => userService.getUserProfile(currentUserId!),
    enabled: !!currentUserId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook: Create user profile
 */
export const useCreateUserProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: {
        name: string;
        email: string;
        phone?: string;
        photoURL?: string;
      };
    }) => userService.createUserProfile(userId, data),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: ['user-profile', userId] });
      qc.invalidateQueries({ queryKey: ['current-user'] });
      toast.success('Profile created successfully');
    },
    onError: (err: any) => toast.error(err?.message || 'Failed to create profile'),
  });
};

/**
 * Hook: Update user profile
 */
export const useUpdateUserProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: Partial<Omit<UserProfile, 'id' | 'createdAt' | 'updatedAt' | 'role'>>;
    }) => userService.updateUserProfile(userId, data),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: ['user-profile', userId] });
      qc.invalidateQueries({ queryKey: ['current-user'] });
      toast.success('Profile updated successfully');
    },
    onError: (err: any) => toast.error(err?.message || 'Failed to update profile'),
  });
};

/**
 * Hook: Ensure user profile exists (create if not)
 * Call this after successful login/signup
 */
export const useEnsureUserProfile = () => {
  const { firebaseUser, loading } = useAuth();
  const currentUser = firebaseUser;
  const createProfile = useCreateUserProfile();

  useEffect(() => {
    const ensureProfile = async () => {
      if (!currentUser) return;

      try {
        const profile = await userService.getUserProfile(currentUser.uid);
        
        if (!profile) {
          // Create profile if it doesn't exist
          await createProfile.mutateAsync({
            userId: currentUser.uid,
            data: {
              name: currentUser.displayName || 'User',
              email: currentUser.email || '',
              phone: currentUser.phoneNumber || undefined,
              photoURL: currentUser.photoURL || undefined,
            },
          });
        }
      } catch (error) {
        console.error('Error ensuring user profile:', error);
      }
    };

    ensureProfile();
  }, [currentUser?.uid]);
};